import { useEffect, useMemo, useRef, useState } from "react";
import { connectSocket, releaseSocket } from "../../lib/socket-client.js";
import { Button } from "../../components/ui/button.jsx";
import { Input } from "../../components/ui/input.jsx";
import { Textarea } from "../../components/ui/textarea.jsx";
import { Phone, UserRound } from "lucide-react";
import { toast } from "sonner";
import { showPhoneCallDemo } from "../../components/notifications/CallNotification.jsx";

const DEFAULT_DIRECTORY = [
  { id: "contact-alex", name: "Alex Morgan", phone: "+14155550132" },
  { id: "contact-nora", name: "Nora Chen", phone: "+442045770932" },
  { id: "contact-raj", name: "Raj Patel", phone: "+918067890123" },
  { id: "contact-amelia", name: "Amelia Rossi", phone: "+551132457890" },
];

function sanitizePhone(value) {
  const digits = String(value || "").replace(/[^0-9+]/g, "");
  if (digits.startsWith("+") && digits.length > 1) return `+${digits.slice(1).replace(/\D+/g, "")}`;
  return digits.replace(/\D+/g, "");
}

function formatDisplay(phone) {
  const digits = String(phone || "").replace(/\D+/g, "");
  if (!digits) return "";
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits.startsWith("1")) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  if (digits.length > 0) return `+${digits}`;
  return "";
}

export default function DialerPanel({
  defaultCallerId = "Neoin Voice",
  contacts = DEFAULT_DIRECTORY,
  initialNumber = "",
  initialCallerId,
  onNumberChange,
  onCallerIdChange,
  onDial,
}) {
  const [callerId, setCallerId] = useState(initialCallerId ?? defaultCallerId);
  const [dialNumber, setDialNumber] = useState(initialNumber);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const socketRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (socketRef.current) {
        releaseSocket();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (initialNumber === undefined) return;
    setDialNumber(initialNumber);
  }, [initialNumber]);

  useEffect(() => {
    if (initialCallerId === undefined) return;
    const nextCaller = initialCallerId || defaultCallerId;
    setCallerId(nextCaller);
  }, [initialCallerId, defaultCallerId]);

  const quickContacts = useMemo(() => {
    const sanitized = contacts.filter((entry) => entry && entry.phone);
    return sanitized.length ? sanitized : DEFAULT_DIRECTORY;
  }, [contacts]);
  const formattedPreview = formatDisplay(dialNumber);
  const canDial = sanitizePhone(dialNumber).length >= 7 && callerId.trim().length >= 2 && status !== "dialing";

  const handleDirectorySelect = (contact) => {
    const nextNumber = contact.phone || "";
    setDialNumber(nextNumber);
    onNumberChange?.(nextNumber);
    if (contact.name) {
      setCallerId(contact.name);
      onCallerIdChange?.(contact.name);
    }
    setError("");
  };

  const emitSocketDial = async (payload) => {
    if (!socketRef.current) {
      socketRef.current = connectSocket();
    }
    const instance = socketRef.current;

    return new Promise((resolve, reject) => {
      let settled = false;
      instance.emit("voice:dial", payload, (ack) => {
        settled = true;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (ack && ack.ok) {
          resolve();
        } else {
          reject(new Error(ack?.error || "Dial request rejected"));
        }
      });
      timeoutRef.current = setTimeout(() => {
        if (!settled) {
          timeoutRef.current = null;
          reject(new Error("Dial request timed out"));
        }
      }, 5000);
    });
  };

  const requestDial = async () => {
    const phone = sanitizePhone(dialNumber);
    if (phone.length < 7) {
      setError("Enter a valid phone number with country code");
      return;
    }
    setStatus("dialing");
    setError("");
    const payload = {
      phone: phone.startsWith("+") ? phone : `+${phone}`,
      callerId: callerId.trim(),
      notes: notes.trim() || undefined,
    };

    try {
      const res = await fetch("/api/v1/voice/dial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("dial_api_failure");
      toast.success("Dial request sent");
    } catch (apiErr) {
      try {
        await emitSocketDial(payload);
        toast.success("Dial request sent via realtime bridge");
      } catch (socketErr) {
        setStatus("error");
        setError(socketErr.message || "Unable to reach dial service");
        return;
      }
    }

    showPhoneCallDemo({
      fromName: payload.callerId,
      phone: payload.phone,
      room: `voice:${payload.phone.replace(/[^0-9]/g, "")}`,
      onApprove: () => onDial?.(payload),
    });
    setStatus("idle");
    onDial?.(payload);
    setNotes("");
    setDialNumber("");
    onNumberChange?.("");
    const resetCaller = initialCallerId ?? defaultCallerId;
    setCallerId(resetCaller);
    onCallerIdChange?.(resetCaller);
  };

  return (
    <div className="glass-card rounded-3xl p-6 text-sm">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-primary">
          <Phone className="h-4 w-4" />
        </span>
        Outbound dialer
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        Call teammates or external partners who are not yet online. We bridge the call through the Neoin socket infrastructure and your configured PSTN provider.
      </p>

      <div className="mt-4 space-y-3">
        <label className="block text-xs font-medium text-muted-foreground">
          Phone number
          <Input
            inputMode="tel"
            autoComplete="tel"
            placeholder="+1 415 555 0132"
            value={dialNumber}
            onChange={(event) => {
              setDialNumber(event.target.value);
              onNumberChange?.(event.target.value);
              setError("");
            }}
            className="mt-1"
          />
        </label>

        <label className="block text-xs font-medium text-muted-foreground">
          Caller ID label
          <Input
            placeholder="Neoin Voice"
            value={callerId}
            onChange={(event) => {
              setCallerId(event.target.value);
              onCallerIdChange?.(event.target.value);
            }}
            className="mt-1"
          />
        </label>

        <label className="block text-xs font-medium text-muted-foreground">
          Notes for the agent (optional)
          <Textarea
            rows={3}
            placeholder="Context for the call, agenda, or follow-ups"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="mt-1"
          />
        </label>

        {formattedPreview && (
          <div className="rounded-2xl border border-border bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            Dial preview: <span className="font-medium text-foreground">{formattedPreview}</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-2 text-xs text-destructive">
            {error}
          </div>
        )}

        <Button type="button" size="sm" onClick={requestDial} disabled={!canDial} className="w-full">
          {status === "dialing" ? "Connecting…" : "Dial now"}
        </Button>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-4">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <UserRound className="h-3.5 w-3.5" /> Quick directory
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {quickContacts.map((contact) => (
            <button
              key={contact.id}
              type="button"
              onClick={() => handleDirectorySelect(contact)}
              className="flex flex-col rounded-2xl border border-border bg-background/40 px-4 py-3 text-left transition hover:border-primary/60 hover:bg-primary/5"
            >
              <span className="text-sm font-semibold text-foreground">{contact.name}</span>
              {contact.phone && <span className="text-xs text-muted-foreground">{contact.phone}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
